import { Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";
import { cn } from "@/lib/utils";

interface FavoriteButtonProps {
  propertyId: string;
  title: string;
  variant?: "overlay" | "inline";
  className?: string;
}

/**
 * Shared favorite/wishlist toggle button.
 * - `overlay`: floating pill for use on top of a property image (PropertyCard).
 * - `inline`: solid coral heart for lists where the item is already a favorite (Wishlist).
 */
const FavoriteButton = ({ propertyId, title, variant = "overlay", className }: FavoriteButtonProps) => {
  const { isFavorite, toggleFavorite, isLoggedIn, isToggling } = useFavorites();
  const navigate = useNavigate();
  const fav = isFavorite(propertyId);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      navigate("/guest/auth", { state: { from: window.location.pathname } });
      return;
    }
    toggleFavorite(propertyId);
  };

  const label = fav ? `Remove ${title} from wishlist` : `Save ${title} to wishlist`;

  if (variant === "inline") {
    return (
      <button
        onClick={handleClick}
        disabled={isToggling}
        className={cn("p-2 text-coral hover:text-coral/80 transition-colors", className)}
        aria-label={label}
        aria-pressed={fav}
        title={fav ? "Remove from wishlist" : "Save to wishlist"}
      >
        <Heart aria-hidden="true" className={cn("h-5 w-5", fav && "fill-current")} />
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={isToggling}
      className={cn(
        "absolute top-4 left-4 p-2 rounded-full bg-card/80 backdrop-blur-sm hover:bg-card transition-colors",
        className,
      )}
      aria-label={label}
      aria-pressed={fav}
      title={fav ? "Remove from wishlist" : "Save to wishlist"}
    >
      <Heart
        aria-hidden="true"
        className={cn(
          "h-4 w-4 transition-colors",
          fav ? "fill-coral text-coral" : "text-muted-foreground",
        )}
      />
    </button>
  );
};

export default FavoriteButton;
